// =============================================================================
// Origin UI — src/pages/compliance/AttestationFlow.tsx
// CM-8 — the gated attestation flow (Code of Conduct showcase).
// Ordered, individually-enabled steps from the item's gating config:
//   1. Watch video  — embedded Synthesia iframe + "I have watched" tick.
//                      (Best-effort: cross-origin iframe can't report play state,
//                       so we detect the click into the player via window blur and
//                       require an explicit confirmation tick.)
//   2. Read document — Code of Conduct PDF, fetched via the authenticated
//                      download endpoint and opened full-page in a new browser tab.
//                      "Opened" is tracked reliably (we control the fetch).
//   3. Complete form — the acknowledgement, rendered by FormRenderer (CM-7).
// Each step is locked until the prior enabled steps are satisfied, and Submit
// stays disabled until every enabled step is satisfied.
//
// COC → COI redirect: if the employee indicates they have a conflict of
// interest (hasConflicts = "Yes"), the success panel prompts them to complete
// the Conflict of Interest Declaration Form.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { complianceApi } from '@/services/complianceApi';
import type {
  ComplianceItemDetail,
  ComplianceFormField,
  ComplianceItemListItem,
} from '@/services/complianceApi';
import {
  FormRenderer,
  validateAnswers,
  collectAnswers,
} from '@/components/compliance/FormRenderer';
import type { AnswerMap } from '@/components/compliance/FormRenderer';
import styles from './AttestationFlow.module.css';

export function AttestationFlow() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();

  const [item, setItem] = useState<ComplianceItemDetail | null>(null);
  const [fields, setFields] = useState<ComplianceFormField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Step state
  const [videoWatched, setVideoWatched] = useState(false);
  const [videoOpened, setVideoOpened] = useState(false); // focus-loss signal (visual)
  const [documentOpened, setDocumentOpened] = useState(false);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState<string | null>(null);

  // Form state
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  // COI redirect state
  const [declaredConflict, setDeclaredConflict] = useState(false);
  const [coiItemId, setCoiItemId] = useState<string | null>(null);

  // ─── Load the item + parse its form schema ───
  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;

    async function load() {
      try {
        const detail = await complianceApi.getItem(itemId!);
        if (cancelled) return;
        setItem(detail);

        if (detail.formSchema) {
          try {
            const parsed = JSON.parse(detail.formSchema) as ComplianceFormField[];
            setFields(Array.isArray(parsed) ? parsed : []);
          } catch {
            setFields([]);
            setError('This form could not be loaded. Please contact Compliance.');
          }
        }
      } catch (err) {
        console.warn('Failed to load attestation item', err);
        if (!cancelled) setError('Could not load this item.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [itemId]);

  // ─── Detect the click into the Synthesia player (window blur on iframe) ───
  useEffect(() => {
    function onBlur() {
      if (document.activeElement?.tagName === 'IFRAME') setVideoOpened(true);
    }
    window.addEventListener('blur', onBlur);
    return () => window.removeEventListener('blur', onBlur);
  }, []);

  const openDocument = useCallback(async () => {
    if (!itemId || docLoading) return;
    setDocLoading(true);
    setDocError(null);
    try {
      await complianceApi.openDocument(itemId);
      setDocumentOpened(true);
    } catch (err) {
      console.warn('Failed to open document', err);
      setDocError('The document could not be opened. Please try again.');
    } finally {
      setDocLoading(false);
    }
  }, [itemId, docLoading]);

  // ─── Steps ───
  const enabledSteps = useMemo(
    () =>
      (item?.gatingSteps ?? [])
        .filter((s) => s.enabled)
        .sort((a, b) => a.order - b.order),
    [item],
  );

  const formValid = useMemo(
    () => fields.length === 0 || Object.keys(validateAnswers(fields, answers)).length === 0,
    [fields, answers],
  );

  const isSatisfied = useCallback(
    (step: string): boolean => {
      if (step === 'Video') return videoWatched;
      if (step === 'Document') return documentOpened;
      if (step === 'Form') return formValid;
      return true;
    },
    [videoWatched, documentOpened, formValid],
  );

  const isUnlocked = useCallback(
    (index: number): boolean =>
      enabledSteps.slice(0, index).every((s) => isSatisfied(s.step)),
    [enabledSteps, isSatisfied],
  );

  const allSatisfied = useMemo(
    () => enabledSteps.every((s) => isSatisfied(s.step)),
    [enabledSteps, isSatisfied],
  );

  async function handleSubmit() {
    if (!item) return;
    const errs = validateAnswers(fields, answers);
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      await complianceApi.submit({
        complianceItemId: item.complianceItemId,
        answers: collectAnswers(fields, answers) as Record<string, unknown>,
      });
      setSubmitted(true);

      // Check if the user declared a conflict of interest
      const conflictAnswer = answers['hasConflicts'];
      if (
        typeof conflictAnswer === 'string' &&
        conflictAnswer.toLowerCase().includes('yes')
      ) {
        setDeclaredConflict(true);
        // Find the COI declaration item
        try {
          const items = await complianceApi.getItems();
          const coi = items.find(
            (i: ComplianceItemListItem) => i.code === 'COI' && i.itemType === 'Declaration',
          );
          if (coi) setCoiItemId(coi.complianceItemId);
        } catch {
          // Non-critical — the redirect button just won't appear
        }
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Submission could not be completed.';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  }

  // ─── Render ───
  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading&hellip;</div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <button className={styles.backLink} onClick={() => navigate('/compliance')}>
            ‹ Back to Compliance
          </button>
          <h1 className={styles.title}>Compliance</h1>
        </header>
        <div className={styles.card}>
          <div className={styles.error}>{error ?? 'Item not found.'}</div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>{item.title}</h1>
        </header>
        <div className={styles.card}>
          <div className={styles.successPanel}>
            <div className={styles.successTick}>✓</div>
            <h2 className={styles.successTitle}>Acknowledgement recorded</h2>
            <p className={styles.successText}>
              Thank you. Your acknowledgement has been submitted and recorded.
            </p>

            {declaredConflict && (
              <div style={{
                marginTop: 20,
                padding: '16px 20px',
                background: '#FFF8E1',
                borderRadius: 8,
                border: '1px solid #FFE082',
                textAlign: 'left',
              }}>
                <p style={{ margin: '0 0 8px', fontWeight: 600, color: '#0A2240' }}>
                  Conflict of Interest Declaration Required
                </p>
                <p style={{ margin: '0 0 12px', color: '#333', fontSize: 14 }}>
                  You indicated that you have a potential conflict of interest. Please complete the
                  Conflict of Interest Declaration Form so that Compliance can review your disclosure.
                </p>
                {coiItemId ? (
                  <button
                    className={styles.primaryBtn}
                    onClick={() => navigate(`/compliance/declaration/${coiItemId}`)}
                  >
                    Complete COI Declaration
                  </button>
                ) : (
                  <button
                    className={styles.primaryBtn}
                    onClick={() => navigate('/compliance')}
                  >
                    Go to Compliance
                  </button>
                )}
              </div>
            )}

            {!declaredConflict && (
              <button
                className={styles.primaryBtn}
                onClick={() => navigate('/compliance')}
              >
                Back to Compliance
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button className={styles.backLink} onClick={() => navigate('/compliance')}>
          ‹ Back to Compliance
        </button>
        <h1 className={styles.title}>{item.title}</h1>
        <p className={styles.subtitle}>
          Complete the steps below in order. All steps must be finished before you
          can submit.
        </p>
      </header>

      <div className={styles.steps}>
        {enabledSteps.map((s, index) => {
          const unlocked = isUnlocked(index);
          const satisfied = isSatisfied(s.step);
          const stepNo = index + 1;

          return (
            <section
              key={`${s.step}-${s.order}`}
              className={`${styles.step} ${unlocked ? '' : styles.stepLocked}`}
            >
              <div className={styles.stepHeader}>
                <span
                  className={`${styles.stepBadge} ${
                    satisfied ? styles.stepBadgeDone : ''
                  }`}
                >
                  {satisfied ? '✓' : stepNo}
                </span>
                <h2 className={styles.stepTitle}>
                  {s.step === 'Video' && 'Watch the training video'}
                  {s.step === 'Document' && 'Read the document'}
                  {s.step === 'Form' && 'Complete the acknowledgement'}
                  {!['Video', 'Document', 'Form'].includes(s.step) && s.step}
                </h2>
              </div>

              {!unlocked ? (
                <div className={styles.lockedHint}>
                  Complete the previous step to continue.
                </div>
              ) : (
                <div className={styles.stepBody}>
                  {/* ── Video ── */}
                  {s.step === 'Video' && (
                    <>
                      {item.synthesiaVideoId ? (
                        <div className={styles.videoWrap}>
                          <iframe
                            className={styles.videoFrame}
                            src={`https://share.synthesia.io/embeds/videos/${item.synthesiaVideoId}`}
                            title="Training video"
                            allow="encrypted-media; fullscreen"
                            allowFullScreen
                          />
                        </div>
                      ) : (
                        <div className={styles.note}>
                          No training video is configured for this item.
                        </div>
                      )}
                      {videoOpened && (
                        <p className={styles.openedHint}>✓ Video opened</p>
                      )}
                      <label className={styles.checkboxRow}>
                        <input
                          type="checkbox"
                          checked={videoWatched}
                          onChange={(e) => setVideoWatched(e.target.checked)}
                        />
                        <span>I have watched the training video.</span>
                      </label>
                    </>
                  )}

                  {/* ── Document ── */}
                  {s.step === 'Document' && (
                    <>
                      <button
                        className={styles.primaryBtn}
                        onClick={openDocument}
                        disabled={docLoading}
                      >
                        {docLoading
                          ? 'Opening…'
                          : documentOpened
                            ? 'Open the document again'
                            : 'Open the document'}
                      </button>
                      {docError && <p className={styles.error}>{docError}</p>}
                      {documentOpened && (
                        <p className={styles.openedHint}>
                          ✓ Document opened in a new tab
                        </p>
                      )}
                    </>
                  )}

                  {/* ── Form ── */}
                  {s.step === 'Form' && (
                    <FormRenderer
                      fields={fields}
                      values={answers}
                      onChange={setAnswers}
                      errors={formErrors}
                      disabled={submitting}
                    />
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* ── Submit ── */}
      <div className={styles.submitBar}>
        {submitError && <p className={styles.error}>{submitError}</p>}
        <button
          className={styles.primaryBtn}
          onClick={handleSubmit}
          disabled={!allSatisfied || submitting}
        >
          {submitting ? 'Submitting…' : 'Submit acknowledgement'}
        </button>
        {!allSatisfied && (
          <p className={styles.note}>
            Finish every step above to enable submission.
          </p>
        )}
      </div>
    </div>
  );
}
